export type Role = 'admin' | 'editor' | 'reports';

export const Roles: Record<Role, string> = {
  admin: 'Administrator',
  editor: 'Editor',
  reports: 'Reports Only'
} as const;

export const Permissions = {
  TEAM_INVITE: ['admin'] as Role[],
  SYNC_SETUP: ['admin'] as Role[],
  VENDOR_EDIT: ['admin', 'editor'] as Role[],
  VIEW_REPORTS: ['admin', 'editor', 'reports'] as Role[],
  /** View markdowns and Overstock monitoring columns */
  MARKDOWN_VIEW: ['admin', 'editor', 'reports'] as Role[],
  /** Preview, apply, reprice, revert, or end Shopify markdowns */
  MARKDOWN_EDIT: ['admin', 'editor'] as Role[],
  TEAM_MEMBER_MANAGE: ['admin'] as Role[],
  TEAM_SETTINGS: ['admin'] as Role[],
  REPORT_SCHEDULE: ['admin', 'editor'] as Role[],
  /** List/resume Receiving bills and sessions (read-only) */
  RECEIVING_VIEW: ['admin', 'editor', 'reports'] as Role[],
  /** Start sessions, scan, assign barcode/weight, complete/cancel/resume/reset */
  RECEIVING_EDIT: ['admin', 'editor'] as Role[],
  /** Edit Xero purchase-order headers and catalog-backed lines */
  PURCHASE_ORDER_EDIT: ['admin', 'editor'] as Role[],
  /** See Imports: price lists, costings, shipments and landed costs */
  IMPORTS_VIEW: ['admin', 'editor', 'reports'] as Role[],
  /** Upload price lists, decide ranges, cost, plan shipments, write Shopify drafts, prices and Xero POs and bills (Imports Q20) */
  IMPORTS_EDIT: ['admin', 'editor'] as Role[],
  /** Search/view Wholesale packing sessions (read-only) */
  WHOLESALE_VIEW: ['admin', 'editor', 'reports'] as Role[],
  /** Start packing sessions, scan, override qty, reset; set wholesale pricing; portal customers and orders */
  WHOLESALE_EDIT: ['admin', 'editor'] as Role[],
  /** View product content drafts / queued SEO changes */
  PRODUCT_CONTENT_VIEW: ['admin', 'editor', 'reports'] as Role[],
  /** Stage, discard, and commit product content changes to Shopify */
  PRODUCT_CONTENT_EDIT: ['admin', 'editor'] as Role[],
  /** Manage AI prompts, playground, and call-log detail */
  AI_PROMPTS_MANAGE: ['admin'] as Role[],
  /** See, create and edit tasks on the team Board (everyone in the team) */
  TASKS: ['admin', 'editor', 'reports'] as Role[],
  /** Rename, reorder, add and remove Board Columns and Labels; connect Slack */
  TASKS_MANAGE: ['admin'] as Role[],
  /** Read and work the shared inbox: claim, assign, status, lanes, Sender rules */
  INBOX: ['admin', 'editor'] as Role[],
  /** Set up the team's Google project, connect or disconnect the mailbox, change inbox switches */
  INBOX_MANAGE: ['admin'] as Role[],
  /** Read Policies & Procedures (everyone in the team) */
  POLICIES: ['admin', 'editor', 'reports'] as Role[],
  /** Write and restore policies; add, rename and reorder Sections */
  POLICIES_EDIT: ['admin', 'editor'] as Role[],
  /** Archive and unarchive policies */
  POLICIES_ARCHIVE: ['admin'] as Role[],
  /** See Social picks: images ranked for posting */
  SOCIAL_PICKS: ['admin', 'editor', 'reports'] as Role[],
  /** Shortlist or reject images in Social picks */
  SOCIAL_PICKS_EDIT: ['admin', 'editor'] as Role[],
  /** Start AI tagging of the image library (costs OpenRouter credit) */
  SOCIAL_PICKS_TAG: ['admin'] as Role[],
  /** Prepare and publish post ideas on the team's Instagram (public, can't be undone from jflo) */
  SOCIAL_PICKS_PUBLISH: ['admin'] as Role[],
  /** Day-to-day marketing edits: Meta post ads, ad recipes, Google feed placements and overrides (settings and syncs need SYNC_SETUP) */
  MARKETING_EDIT: ['admin', 'editor'] as Role[],
  /** Change collections: draft edits, tree and menu changes, commits to Shopify */
  COLLECTIONS_EDIT: ['admin', 'editor'] as Role[],
  /** Create, update and delete the team's saved views (saved filters) */
  SAVED_VIEWS_EDIT: ['admin', 'editor'] as Role[],
} as const;

export type Permission = keyof typeof Permissions;

/**
 * Route-level Receiving API permission map.
 * Worker handlers must call requirePermission(...) with these values;
 * frontend checks only control presentation (hide/disable actions).
 */
export const ReceivingRoutePermissions = {
  /** Draft bills and purchase orders to receive */
  listDocuments: 'RECEIVING_VIEW',
  refreshDocuments: 'RECEIVING_EDIT',
  listSessions: 'RECEIVING_VIEW',
  createSession: 'RECEIVING_EDIT',
  getSession: 'RECEIVING_VIEW',
  refreshSession: 'RECEIVING_EDIT',
  scan: 'RECEIVING_EDIT',
  overrideItemQty: 'RECEIVING_EDIT',
  setItemDamagedQty: 'RECEIVING_EDIT',
  incrementItemDamaged: 'RECEIVING_EDIT',
  setItemBarcode: 'RECEIVING_EDIT',
  setItemWeight: 'RECEIVING_EDIT',
  /** Unknown products (T-286): pick, link and photograph */
  listLinkCandidates: 'RECEIVING_VIEW',
  linkItemProduct: 'RECEIVING_EDIT',
  setItemPhoto: 'RECEIVING_EDIT',
  completeSession: 'RECEIVING_EDIT',
  cancelSession: 'RECEIVING_EDIT',
  resumeSession: 'RECEIVING_EDIT',
  resetSession: 'RECEIVING_EDIT',
  updateSessionNotes: 'RECEIVING_EDIT',
  updateItemNotes: 'RECEIVING_EDIT',
  uploadAttachment: 'RECEIVING_EDIT',
  deleteAttachment: 'RECEIVING_EDIT',
  extractInvoiceDetails: 'RECEIVING_EDIT',
  getReview: 'RECEIVING_VIEW',
  patchReview: 'RECEIVING_EDIT',
  discardReview: 'RECEIVING_EDIT',
  writeReviewDrafts: 'RECEIVING_EDIT',
  markReviewPoBilled: 'RECEIVING_EDIT',
  reopenReview: 'RECEIVING_EDIT',
  closePurchaseOrder: 'RECEIVING_EDIT',
  /** Held aside (T-285): anyone who can write a receiving review decides. */
  listHeldAside: 'RECEIVING_VIEW',
  decideHeldAside: 'RECEIVING_EDIT',
  billHeldAside: 'RECEIVING_EDIT',
  collectHeldAside: 'RECEIVING_EDIT',
  reopenHeldAside: 'RECEIVING_EDIT',
  /** "Link product" on an unknown product held aside (T-286), as in the receiving. */
  listHeldAsideLinkCandidates: 'RECEIVING_VIEW',
  linkHeldAsideProduct: 'RECEIVING_EDIT',
  /** The request email's draft shows the team's mailbox and the supplier's addresses. */
  getHeldAsideInvoiceRequest: 'RECEIVING_EDIT',
  sendHeldAsideInvoiceRequest: 'RECEIVING_EDIT',
} as const satisfies Record<string, Permission>;

export type ReceivingRoutePermissionKey = keyof typeof ReceivingRoutePermissions;

export const PurchaseOrderRoutePermissions = {
  list: 'VIEW_REPORTS',
  lines: 'VIEW_REPORTS',
  getEditDetail: 'PURCHASE_ORDER_EDIT',
  update: 'PURCHASE_ORDER_EDIT',
  /** Email it to the supplier from the team inbox (T-134) */
  email: 'PURCHASE_ORDER_EDIT',
} as const satisfies Record<string, Permission>;

export type PurchaseOrderRoutePermissionKey =
  keyof typeof PurchaseOrderRoutePermissions;

/** `/api/planning`: the report and saved planning orders read; saving and Xero drafts edit. */
export const PlanningRoutePermissions = {
  report: 'VIEW_REPORTS',
  listOrders: 'VIEW_REPORTS',
  getOrder: 'VIEW_REPORTS',
  saveOrder: 'PURCHASE_ORDER_EDIT',
  updateOrder: 'PURCHASE_ORDER_EDIT',
  discardOrder: 'PURCHASE_ORDER_EDIT',
  saveSupplierStockOverride: 'PURCHASE_ORDER_EDIT',
  clearSupplierStockOverride: 'PURCHASE_ORDER_EDIT',
  createDraftPurchaseOrder: 'PURCHASE_ORDER_EDIT',
} as const satisfies Record<string, Permission>;

/**
 * Route-level Wholesale packing API permission map.
 * Worker handlers must call requirePermission(...) with these values;
 * frontend checks only control presentation (hide/disable actions).
 */
export const WholesaleRoutePermissions = {
  searchInvoices: 'WHOLESALE_VIEW',
  refreshInvoices: 'WHOLESALE_VIEW',
  createSession: 'WHOLESALE_EDIT',
  getSession: 'WHOLESALE_VIEW',
  scan: 'WHOLESALE_EDIT',
  overrideItemQty: 'WHOLESALE_EDIT',
  resetSession: 'WHOLESALE_EDIT',
  listPricingBrands: 'WHOLESALE_VIEW',
  getPricing: 'WHOLESALE_VIEW',
  getPriceList: 'WHOLESALE_VIEW',
  savePricing: 'WHOLESALE_EDIT',
  importPricing: 'WHOLESALE_EDIT',
  // B2B portal (T-146): customers, their buyers, and portal orders
  listCustomers: 'WHOLESALE_VIEW',
  saveCustomer: 'WHOLESALE_EDIT',
  manageBuyers: 'WHOLESALE_EDIT',
  listOrders: 'WHOLESALE_VIEW',
  getOrder: 'WHOLESALE_VIEW',
  editOrder: 'WHOLESALE_EDIT',
  confirmOrder: 'WHOLESALE_EDIT',
  cancelOrder: 'WHOLESALE_EDIT',
  // Orders staff make in jflo for a customer (T-15), prices set by hand included
  createOrder: 'WHOLESALE_EDIT',
  // The team's minimum order and delivery terms (T-182)
  saveOrderTerms: 'WHOLESALE_EDIT',
  // Trade customers (T-218): discounts off retail, and creating a customer's Xero contact
  listTradeDiscounts: 'WHOLESALE_VIEW',
  saveTradeDiscounts: 'WHOLESALE_EDIT',
  createXeroContact: 'WHOLESALE_EDIT',
  // Delivery addresses and collection (T-10)
  listCustomerAddresses: 'WHOLESALE_VIEW',
  saveCustomerAddress: 'WHOLESALE_EDIT',
  saveCollection: 'WHOLESALE_EDIT',
  // The portal's order emails (T-10): their messages, and previews of them
  saveOrderEmails: 'WHOLESALE_EDIT',
  previewOrderEmail: 'WHOLESALE_VIEW',
  // Which Xero sales accounts portal invoices post to: admins only, like the domain
  saveSalesAccounts: 'TEAM_SETTINGS',
  // The portal's domain (Cloudflare and DNS): admins only
  managePortalDomain: 'TEAM_SETTINGS',
} as const satisfies Record<string, Permission>;

export type WholesaleRoutePermissionKey = keyof typeof WholesaleRoutePermissions;

/**
 * Route-level Product Content API permission map.
 * Worker handlers must call requirePermission(...) with these values.
 */
export const ProductContentRoutePermissions = {
  getDraftSummary: 'PRODUCT_CONTENT_VIEW',
  getDraft: 'PRODUCT_CONTENT_VIEW',
  getCommitStatus: 'PRODUCT_CONTENT_VIEW',
  stageSeo: 'PRODUCT_CONTENT_EDIT',
  stageTeaser: 'PRODUCT_CONTENT_EDIT',
  stageDescription: 'PRODUCT_CONTENT_EDIT',
  stageProductType: 'PRODUCT_CONTENT_EDIT',
  stageCollectionContent: 'PRODUCT_CONTENT_EDIT',
  discardChange: 'PRODUCT_CONTENT_EDIT',
  discardDraft: 'PRODUCT_CONTENT_EDIT',
  commit: 'PRODUCT_CONTENT_EDIT',
  previewShopifyStatusLive: 'PRODUCT_CONTENT_EDIT',
  patchShopifyStatusLive: 'PRODUCT_CONTENT_EDIT',
  /** Upload, crop, AI-edit and restore a product's Shopify images (T-142); writes to Shopify. */
  editProductMedia: 'PRODUCT_CONTENT_EDIT',
  /** Alt text for product images (T-165): suggestions and automatic saving, on the Media page. */
  altTextStatus: 'PRODUCT_CONTENT_VIEW',
  /** Suggest alt text, save it to Shopify, and switch automatic saving on or off (T-165). */
  editAltText: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

export type ProductContentRoutePermissionKey = keyof typeof ProductContentRoutePermissions;

/** A live product's remembered Supplier Content, used when generating copy on its product page. */
export const ProductSupplierContentRoutePermissions = {
  get: 'PRODUCT_CONTENT_VIEW',
  save: 'PRODUCT_CONTENT_EDIT',
  extractText: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

/**
 * Route-level AI Prompts admin API permission map.
 * Worker handlers must call requirePermission(...) with these values.
 */
export const AiPromptsRoutePermissions = {
  list: 'AI_PROMPTS_MANAGE',
  getByOperation: 'AI_PROMPTS_MANAGE',
  createVersion: 'AI_PROMPTS_MANAGE',
  patch: 'AI_PROMPTS_MANAGE',
  testContext: 'AI_PROMPTS_MANAGE',
  test: 'AI_PROMPTS_MANAGE',
  sampleProducts: 'AI_PROMPTS_MANAGE',
  sampleCollections: 'AI_PROMPTS_MANAGE',
  listTestRuns: 'AI_PROMPTS_MANAGE',
  getTestRun: 'AI_PROMPTS_MANAGE',
  listLogs: 'AI_PROMPTS_MANAGE',
  getLog: 'AI_PROMPTS_MANAGE',
  /** The AI usage report (/ai-usage): cost and calls by prompt, version, model and person. */
  usage: 'AI_PROMPTS_MANAGE',
} as const satisfies Record<string, Permission>;

export type AiPromptsRoutePermissionKey = keyof typeof AiPromptsRoutePermissions;

/**
 * Route-level live AI generation permission map.
 */
export const AiGenerateRoutePermissions = {
  generateProductSeo: 'PRODUCT_CONTENT_EDIT',
  generateProductTeaser: 'PRODUCT_CONTENT_EDIT',
  generateProductContent: 'PRODUCT_CONTENT_EDIT',
  generateBatchProductContent: 'PRODUCT_CONTENT_EDIT',
  generateBatchProductSeo: 'PRODUCT_CONTENT_EDIT',
  generateBatchProductTeaser: 'PRODUCT_CONTENT_EDIT',
  tidySupplierContent: 'PRODUCT_CONTENT_EDIT',
  generateCollectionContent: 'PRODUCT_CONTENT_EDIT',
  /** Turn down generated text without saving it (T-78). */
  dismissSuggestions: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

export type AiGenerateRoutePermissionKey = keyof typeof AiGenerateRoutePermissions;

export const MarkdownRoutePermissions = {
  preview: 'MARKDOWN_EDIT',
  apply: 'MARKDOWN_EDIT',
  list: 'MARKDOWN_VIEW',
  getByVariant: 'MARKDOWN_VIEW',
  revert: 'MARKDOWN_EDIT',
  reprice: 'MARKDOWN_EDIT',
  revertSettings: 'MARKDOWN_EDIT',
  schedule: 'MARKDOWN_EDIT',
  end: 'MARKDOWN_EDIT',
  adoptPreview: 'MARKDOWN_EDIT',
  adopt: 'MARKDOWN_EDIT',
  clearSale: 'MARKDOWN_EDIT',
} as const satisfies Record<string, Permission>;

export type MarkdownRoutePermissionKey = keyof typeof MarkdownRoutePermissions;

/**
 * Route-level New Product Batches API permission map.
 */
export const ProductBatchRoutePermissions = {
  list: 'PRODUCT_CONTENT_VIEW',
  get: 'PRODUCT_CONTENT_VIEW',
  getCommitStatus: 'PRODUCT_CONTENT_VIEW',
  write: 'PRODUCT_CONTENT_EDIT',
  commit: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

/**
 * Route-level Tasks API permission map. Everyone in a team can see and edit tasks;
 * Board columns and the Slack connection are admin-only.
 */
export const TaskRoutePermissions = {
  view: 'TASKS',
  edit: 'TASKS',
  manage: 'TASKS_MANAGE',
} as const satisfies Record<string, Permission>;

/**
 * Route-level Policies & Procedures permission map (T-93).
 */
/** Imports (T-236, docs/import-costing-design.md). Q20: anyone who edits can act, behind a change preview. */
export const ImportsRoutePermissions = {
  view: 'IMPORTS_VIEW',
  edit: 'IMPORTS_EDIT',
} as const satisfies Record<string, Permission>;

export const PolicyRoutePermissions = {
  view: 'POLICIES',
  edit: 'POLICIES_EDIT',
  archive: 'POLICIES_ARCHIVE',
} as const satisfies Record<string, Permission>;

/**
 * Route-level shared inbox permission map (docs/shared-inbox-plan.md).
 */
export const InboxRoutePermissions = {
  view: 'INBOX',
  edit: 'INBOX',
  manage: 'INBOX_MANAGE',
} as const satisfies Record<string, Permission>;

export type ProductBatchRoutePermissionKey = keyof typeof ProductBatchRoutePermissions;

/**
 * Route-level Product Types catalog API permission map.
 */
export const ProductTypesRoutePermissions = {
  list: 'PRODUCT_CONTENT_VIEW',
  get: 'PRODUCT_CONTENT_VIEW',
  create: 'PRODUCT_CONTENT_EDIT',
  patch: 'PRODUCT_CONTENT_EDIT',
  delete: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

export type ProductTypesRoutePermissionKey = keyof typeof ProductTypesRoutePermissions;

/** Product type nouns and the type builder (T-329): read with product content, set up by its editors. */
export const ProductTypeNounsRoutePermissions = {
  list: 'PRODUCT_CONTENT_VIEW',
  get: 'PRODUCT_CONTENT_VIEW',
  find: 'PRODUCT_CONTENT_EDIT',
  save: 'PRODUCT_CONTENT_EDIT',
  suggest: 'PRODUCT_CONTENT_EDIT',
  classify: 'PRODUCT_CONTENT_EDIT',
  apply: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

/** Colour swatches for the storefront theme (T-267): read with product content, written by its editors. */
export const SwatchRoutePermissions = {
  list: 'PRODUCT_CONTENT_VIEW',
  save: 'PRODUCT_CONTENT_EDIT',
  delete: 'PRODUCT_CONTENT_EDIT',
  import: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

/**
 * Supplier stock integrations:
 * VIEW_REPORTS — catalog, history, sync status
 * SYNC_SETUP — credentials, test, sync, archive/replace
 * VENDOR_EDIT — map / unmap / auto-map
 */
export const SupplierStockRoutePermissions = {
  getIntegration: 'VIEW_REPORTS',
  putIntegration: 'SYNC_SETUP',
  previewSheet: 'SYNC_SETUP',
  testIntegration: 'SYNC_SETUP',
  syncIntegration: 'SYNC_SETUP',
  syncStatus: 'VIEW_REPORTS',
  archiveIntegration: 'SYNC_SETUP',
  listItems: 'VIEW_REPORTS',
  itemHistory: 'VIEW_REPORTS',
  listRuns: 'VIEW_REPORTS',
  putMapping: 'VENDOR_EDIT',
  deleteMapping: 'VENDOR_EDIT',
  autoMap: 'VENDOR_EDIT',
  searchVariants: 'VENDOR_EDIT',
  listUnmapped: 'VIEW_REPORTS',
  searchItems: 'VENDOR_EDIT',
} as const satisfies Record<string, Permission>;

export type SupplierStockRoutePermissionKey = keyof typeof SupplierStockRoutePermissions;

/**
 * Products (/api/products): every member reads the catalog and makes label shortcodes;
 * PRODUCT_CONTENT_EDIT changes a variant's statuses or product link and refreshes from Shopify.
 */
export const ProductRoutePermissions = {
  view: 'VIEW_REPORTS',
  labels: 'VIEW_REPORTS',
  edit: 'PRODUCT_CONTENT_EDIT',
} as const satisfies Record<string, Permission>;

/** The Media list (/api/media): every member reads it. Edits go through /api/products. */
export const MediaRoutePermissions = {
  list: 'VIEW_REPORTS',
} as const satisfies Record<string, Permission>;

/** Brands (/api/vendors, and a brand's supplier links): every member reads; VENDOR_EDIT changes them. */
export const VendorRoutePermissions = {
  view: 'VIEW_REPORTS',
  edit: 'VENDOR_EDIT',
} as const satisfies Record<string, Permission>;

/** Suppliers (/api/suppliers): every member reads; VENDOR_EDIT changes them. */
export const SupplierRoutePermissions = {
  view: 'VIEW_REPORTS',
  edit: 'VENDOR_EDIT',
} as const satisfies Record<string, Permission>;

/**
 * Marketing (/api/marketing): every member reads; MARKETING_EDIT changes ads, recipes and the
 * Google feed; SYNC_SETUP starts syncs and backfills and changes marketing settings.
 */
export const MarketingRoutePermissions = {
  view: 'VIEW_REPORTS',
  edit: 'MARKETING_EDIT',
  setup: 'SYNC_SETUP',
} as const satisfies Record<string, Permission>;

/** Integrations (/api/integrations): every member reads the connection status; SYNC_SETUP changes it. */
export const IntegrationRoutePermissions = {
  view: 'VIEW_REPORTS',
  setup: 'SYNC_SETUP',
} as const satisfies Record<string, Permission>;

/** Collections (/api/collections): every member reads; COLLECTIONS_EDIT changes drafts, the tree and menus. */
export const CollectionRoutePermissions = {
  view: 'VIEW_REPORTS',
  edit: 'COLLECTIONS_EDIT',
} as const satisfies Record<string, Permission>;

/** Landing pages (/api/marketing/landing-pages, T-386): every member reads; COLLECTIONS_EDIT edits and publishes. */
export const LandingPageRoutePermissions = {
  view: 'VIEW_REPORTS',
  edit: 'COLLECTIONS_EDIT',
} as const satisfies Record<string, Permission>;

/** Sync (/api/sync): every member reads the history; SYNC_SETUP starts syncs; COLLECTIONS_EDIT the catalog sync. */
export const SyncRoutePermissions = {
  view: 'VIEW_REPORTS',
  setup: 'SYNC_SETUP',
  catalog: 'COLLECTIONS_EDIT',
} as const satisfies Record<string, Permission>;

/** Team settings › API keys (/api/api-keys): admins only, reads included. */
export const ApiKeyRoutePermissions = {
  list: 'TEAM_SETTINGS',
  create: 'TEAM_SETTINGS',
  update: 'TEAM_SETTINGS',
  revoke: 'TEAM_SETTINGS',
} as const satisfies Record<string, Permission>;

/** GA4 BigQuery export (/api/integrations/bigquery): every member reads the status; SYNC_SETUP the rest. */
export const Ga4BigQueryRoutePermissions = {
  view: 'VIEW_REPORTS',
  setup: 'SYNC_SETUP',
} as const satisfies Record<string, Permission>;

/** Chrome extension sign-in (/api/extension/connect): every member may connect the extension. */
export const ExtensionRoutePermissions = {
  connect: 'TASKS',
} as const satisfies Record<string, Permission>;

/** Labels (/api/labels): the page layout and per-variant label preferences, open to every member (decision 1). */
export const LabelRoutePermissions = {
  view: 'VIEW_REPORTS',
  edit: 'VIEW_REPORTS',
} as const satisfies Record<string, Permission>;

/** Saved filters (/api/saved-filters): every member reads and searches; SAVED_VIEWS_EDIT saves, renames and deletes. */
export const SavedFilterRoutePermissions = {
  view: 'VIEW_REPORTS',
  edit: 'SAVED_VIEWS_EDIT',
} as const satisfies Record<string, Permission>;

/** Xero contacts search (/api/xero/contacts): every member reads (supplier and wholesale customer linking). */
export const XeroContactRoutePermissions = {
  search: 'VIEW_REPORTS',
} as const satisfies Record<string, Permission>;

/**
 * Reports (/api/reports): every member reads them; REPORT_SCHEDULE lists, saves and test-sends
 * scheduled report emails. The markdowns report uses `MarkdownRoutePermissions.list`.
 */
export const ReportRoutePermissions = {
  view: 'VIEW_REPORTS',
  schedule: 'REPORT_SCHEDULE',
  /** Supplier Changes' new retail prices (T-288): the same as applying markdowns on Overstock. */
  reprice: 'MARKDOWN_EDIT',
} as const satisfies Record<string, Permission>;

/**
 * Team members and invites (/api/team/members, /api/team/invites): every member reads the list;
 * TEAM_MEMBER_MANAGE changes roles and removes; TEAM_INVITE sends, lists and cancels invites.
 * `profile`: a member's name and photo, open to every member at the route; the handler allows
 * yourself, or anyone with TEAM_MEMBER_MANAGE.
 */
export const TeamMemberRoutePermissions = {
  view: 'VIEW_REPORTS',
  manage: 'TEAM_MEMBER_MANAGE',
  invite: 'TEAM_INVITE',
  profile: 'VIEW_REPORTS',
} as const satisfies Record<string, Permission>;

/**
 * Team settings and branding (/api/team): every member reads them; TEAM_SETTINGS (admins) saves
 * the settings and changes the logos; the Shopify app credentials are SYNC_SETUP (T-212).
 */
export const TeamRoutePermissions = {
  view: 'VIEW_REPORTS',
  settings: 'TEAM_SETTINGS',
  shopifyAppCredentials: 'SYNC_SETUP',
} as const satisfies Record<string, Permission>;

/**
 * Xero posting (/api/xero-posting): every member reads the posting status and the Xero and Shopify
 * lists the settings pages show; SYNC_SETUP retries failed posts.
 */
export const XeroPostingRoutePermissions = {
  status: 'VIEW_REPORTS',
  eligibleCount: 'VIEW_REPORTS',
  paymentMethods: 'VIEW_REPORTS',
  taxTypes: 'VIEW_REPORTS',
  brandingThemes: 'VIEW_REPORTS',
  trackingCategories: 'VIEW_REPORTS',
  paymentAccounts: 'VIEW_REPORTS',
  expenseAccounts: 'VIEW_REPORTS',
  shopifyLocations: 'VIEW_REPORTS',
  stockSyncStatus: 'VIEW_REPORTS',
  retry: 'SYNC_SETUP',
} as const satisfies Record<string, Permission>;

export function hasPermission(userRole: Role, permission: Permission): boolean {
  return Permissions[permission].includes(userRole);
}

export function canManageRole(actorRole: Role, targetRole: Role): boolean {
  if (actorRole !== 'admin') return false;
  return ['admin', 'editor', 'reports'].includes(targetRole);
} 